import React, { useState, useEffect } from 'react'
import { X, Check, Plus, Trash2 } from 'lucide-react'
import { Button, Input, Modal } from '../ui'
import { useQuery } from '@tanstack/react-query'
import { getPaymentMethods } from '../../services/paymentMethodService'

export interface PaymentDetail {
  method: string
  received: number
  amount: number
}

interface PaymentModalProps {
  visible: boolean
  total: number
  onClose: () => void
  onConfirm: (payments: PaymentDetail[]) => void
  loading?: boolean
}

export const PaymentModal: React.FC<PaymentModalProps> = ({
  visible,
  total,
  onClose,
  onConfirm,
  loading,
}) => {
  const [payments, setPayments] = useState<PaymentDetail[]>([])
  const [received, setReceived] = useState<number | ''>(0)
  const [selectedMethod, setSelectedMethod] = useState('')

  const { data: methods } = useQuery({
    queryKey: ['paymentMethods'],
    queryFn: getPaymentMethods,
    enabled: visible
  })

  const totalPaidAmount = payments.reduce((sum, p) => sum + p.amount, 0)
  const pendingAmount = Math.max(0, total - totalPaidAmount)
  const change = payments.reduce((sum, p) => sum + p.received, 0) - total

  // Auto-select first method or specifically 'Efectivo' if exists
  useEffect(() => {
    if (visible && methods && methods.length > 0) {
      const cash = methods.find(m => m.type === 'CASH')
      if (cash) setSelectedMethod(cash.name)
      else setSelectedMethod(methods[0].name)
      setPayments([])
    }
    if (visible && total > 0) setReceived(total)
  }, [visible, methods, total])

  // Auto-fill received amount if method is NOT cash (usually)
  useEffect(() => {
    if (!methods) return
    const method = methods.find(m => m.name === selectedMethod)
    if (method && method.type !== 'CASH') {
      setReceived(pendingAmount > 0 ? pendingAmount : '')
    } else {
      setReceived(pendingAmount > 0 ? pendingAmount : '')
    }
  }, [selectedMethod, pendingAmount, methods])

  const addPayment = () => {
    const recv = Number(received)
    if (recv <= 0 || !selectedMethod) return
    
    // We can't apply more than pendingAmount for the subtotal map, but if they pay with $50 cash we record it.
    const appliedAmount = Math.min(recv, pendingAmount)
    setPayments(prev => [...prev, {
      method: selectedMethod,
      received: recv,
      amount: appliedAmount
    }])
    setReceived('')
  }

  const removePayment = (index: number) => {
    setPayments(prev => prev.filter((_, i) => i !== index))
  }

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    
    const recv = Number(received)
    // If they typed something and hit 'Completar', we assume they want to add it and complete
    if (pendingAmount > 0 && recv > 0 && selectedMethod) {
      const appliedAmount = Math.min(recv, pendingAmount)
      const finalPayments = [...payments, {
        method: selectedMethod,
        received: recv,
        amount: appliedAmount
      }]
      
      const newTotalPaid = finalPayments.reduce((sum, p) => sum + p.amount, 0)
      if (newTotalPaid >= total) {
        onConfirm(finalPayments)
      }
      return
    }

    if (totalPaidAmount >= total) {
      onConfirm(payments)
    }
  }

  return (
    <Modal open={visible} onClose={onClose} titleId="payment-modal-title" className="max-w-md">
      <div className="flex items-start justify-between border-b border-border px-6 py-4">
        <h2 id="payment-modal-title" className="text-lg font-semibold text-text">
          Confirmar Pago
        </h2>
        <Button variant="ghost" size="sm" aria-label="Cerrar" onClick={onClose} className="h-9 w-9 rounded-full">
          <X className="h-4 w-4" aria-hidden />
        </Button>
      </div>
      <form onSubmit={handleSubmit} className="space-y-4 px-6 py-5">
        <div className="flex items-center justify-between text-base">
          <span className="text-text-muted">Total a Pagar</span>
          <span className="font-bold text-xl text-text">${total.toLocaleString()}</span>
        </div>

        {payments.length > 0 && (
          <div className="bg-surface-muted border border-divider rounded-md p-3 space-y-2">
            <h4 className="text-sm font-semibold text-[color:var(--color-text)]">Pagos divididos</h4>
            {payments.map((p, i) => (
              <div key={i} className="flex justify-between items-center text-sm">
                <span className="text-[color:var(--color-text)]">{p.method} <span className="text-muted text-xs">(Rec.: ${p.received.toLocaleString()})</span></span>
                <div className="flex items-center gap-2">
                  <span className="font-medium text-[color:var(--color-text)]">${p.amount.toLocaleString()}</span>
                  <button type="button" onClick={() => removePayment(i)} className="text-red-500 hover:bg-red-500/10 p-1 rounded transition"><Trash2 size={14}/></button>
                </div>
              </div>
            ))}
            <div className="flex justify-between items-center font-bold text-sm border-t border-divider pt-2 mt-2">
              <span className="text-[color:var(--color-text)]">Restante</span>
              <span className={pendingAmount > 0 ? "text-red-500" : "text-green-500"}>${pendingAmount.toLocaleString()}</span>
            </div>
          </div>
        )}

        {pendingAmount > 0 && (
          <>
            <div>
              <label className="block text-sm font-medium text-text mb-2">Medio de Pago</label>
              <div className="grid grid-cols-2 gap-2">
                {methods?.filter(m => m.isActive).map(method => (
                  <button
                    key={method.id}
                    type="button"
                    onClick={() => setSelectedMethod(method.name)}
                    className={`px-3 py-2 rounded-md text-sm font-medium border transition-colors flex items-center justify-between ${selectedMethod === method.name
                        ? 'bg-blue-50 border-blue-500 text-blue-700'
                        : 'bg-white border-border text-text hover:bg-gray-50'
                      }`}
                  >
                    {method.name}
                    {selectedMethod === method.name && <Check size={14} />}
                  </button>
                ))}
                {methods && methods.filter(m => m.isActive).length === 0 && (
                  <p className="col-span-2 text-xs text-red-500 italic">
                    No hay métodos de pago activos. Configúralos en Ajustes.
                  </p>
                )}
                {!methods && (
                  <p className="col-span-2 text-xs text-muted animate-pulse">Cargando métodos...</p>
                )}
              </div>
            </div>

            <div>
              <label htmlFor="payment-received" className="text-sm font-medium text-text">
                Monto Recibido
              </label>
              <div className="flex gap-2 mt-1 items-stretch">
                <Input
                  id="payment-received"
                  type="number"
                  inputMode="numeric"
                  value={received}
                  onChange={(e) => setReceived(e.target.value === '' ? '' : Number(e.target.value))}
                  className="text-lg flex-1"
                  autoFocus
                />
                <Button 
                   type="button" 
                   variant="outline" 
                   onClick={addPayment}
                   disabled={Number(received) <= 0 || !selectedMethod}
                   className="h-auto"
                >
                  <Plus size={18} className="mr-1"/> Agregar
                </Button>
              </div>
            </div>
          </>
        )}

        {change > 0 && totalPaidAmount >= total && (
          <div className="flex items-center justify-between text-sm p-3 rounded-md bg-green-50 text-green-800">
            <span className="font-medium">Cambio / Devuelta</span>
            <span className="font-bold text-lg">${change.toLocaleString()}</span>
          </div>
        )}

        <Button 
          type="submit" 
          disabled={loading || (pendingAmount > 0 && (Number(received) < pendingAmount || !selectedMethod))} 
          isLoading={loading} 
          className="w-full text-lg h-12 mt-4"
        >
          Completar Venta
        </Button>
      </form>
    </Modal>
  )
}

export default PaymentModal
